import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import {
  apiConfigurada,
  crearProyecto,
  listarProyectosFull,
  type ProyectoRow,
} from "../../lib/api.ts";
import { C, st, dinero } from "../../ui/theme.ts";
import { Chip, Fila } from "../../ui/componentes.tsx";
import { IconoProyecto } from "../../ui/iconos.tsx";

/**
 * PROYECTOS — rebanada 3 de la beta. El desarrollo inmobiliario es la unidad
 * central del cumplimiento (Art. 3 fr. IV Bis): a él se ligan captación,
 * compraventas y avisos. La caracterización (¿es Desarrollo Inmobiliario? ¿con
 * qué destino?) se documenta ANTES de recibir el primer peso y queda sellada.
 */

const OPACIDAD_DESHABILITADO = 0.6;

const FINALIDADES: [string, string][] = [
  ["venta", "Venta"],
  ["renta", "Renta (build-to-rent)"],
  ["mixto", "Mixto (venta + renta)"],
];

const TIPOS_DESARROLLO: [string, string][] = [
  ["1", "Habitacional"],
  ["2", "Comercial"],
  ["3", "Oficinas"],
  ["4", "Industrial"],
  ["5", "Mixto"],
  ["99", "Otro"],
];

function fraccionesDe(finalidad: string): string[] {
  const fx = ["V Bis (captación de recursos)"];
  if (finalidad === "venta" || finalidad === "mixto") fx.push("V (venta de unidades)");
  if (finalidad === "renta" || finalidad === "mixto") fx.push("XV (renta) — umbrales mensuales propios");
  return fx;
}

export function ProyectosView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [proyectos, setProyectos] = useState<ProyectoRow[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [modo, setModo] = useState<"detalle" | "nuevo">("detalle");
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);

  const sel = proyectos.find((p) => p.id === selId) ?? null;

  const recargar = useCallback(async () => {
    if (demo) return;
    setProyectos(await listarProyectosFull(token));
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    recargar()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los proyectos."); })
      .finally(() => { setCargando(false); });
  }, [demo, recargar]);

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Lista central */}
      <section className="argus-list-pane" style={{ width: 330, flexShrink: 0, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", background: "#FBFCFB" }}>
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Proyectos</h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
            La unidad central del cumplimiento: a cada desarrollo se ligan captación, ventas y avisos.
          </p>
        </div>
        {demo && (
          <div style={{ margin: "8px 16px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — conecta el backend para operar de verdad.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && proyectos.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>
              Sin proyectos — caracteriza el primero ANTES de recibir el primer peso.
            </p>
          )}
          {proyectos.map((p) => {
            const activa = selId === p.id && modo === "detalle";
            return (
              <button
                key={p.id}
                onClick={() => { setSelId(p.id); setModo("detalle"); setError(null); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>{p.nombre}</div>
                <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 6px" }}>
                  {FINALIDADES.find(([v]) => v === p.finalidad)?.[1] ?? p.finalidad} · {p.jurisdiccion}
                  {p.registro_licencia ? ` · ${p.registro_licencia}` : ""}
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {p.es_desarrollo_inmobiliario
                    ? <Chip tono="verde">✓ Desarrollo Inmobiliario</Chip>
                    : <Chip tono="gris">Fuera de definición</Chip>}
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: `1px solid ${C.line}` }}>
          <button style={{ ...st.btnPrim, width: "100%" }} onClick={() => { setModo("nuevo"); setSelId(null); setError(null); }}>
            ＋ Nuevo proyecto
          </button>
        </div>
      </section>

      {/* Detalle / captura */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {modo === "nuevo" ? (
          <FormProyecto
            demo={demo}
            onCancelar={() => { setModo("detalle"); }}
            onCrear={async (datos) => {
              setError(null);
              try {
                if (demo) {
                  setError("En modo demostración el alta requiere backend conectado.");
                  return;
                }
                const fila = await crearProyecto(token, tenantId, datos);
                await recargar();
                setSelId(fila.id);
                setModo("detalle");
              } catch (e) {
                setError(e instanceof Error ? e.message : "No se pudo crear el proyecto.");
              }
            }}
          />
        ) : sel ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>{sel.nombre}</h2>
            <p style={{ margin: "0 0 10px", color: C.muted, fontSize: 13 }}>
              {FINALIDADES.find(([v]) => v === sel.finalidad)?.[1] ?? sel.finalidad} · {sel.jurisdiccion}
            </p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {sel.es_desarrollo_inmobiliario
                ? <Chip tono="verde">✓ Desarrollo Inmobiliario (Art. 3 fr. IV Bis)</Chip>
                : <Chip tono="gris">Fuera de la definición legal</Chip>}
              {fraccionesDe(sel.finalidad).map((f) => (
                <Chip key={f} tono={f.startsWith("XV") ? "ambar" : "tiel"}>{f}</Chip>
              ))}
            </div>

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Caracterización (sellada en el expediente)</p>
              <div style={{ fontSize: 13.5, color: C.ink }}>
                {sel.justificacion_sellada ?? "Sin justificación registrada — documenta por qué encuadra (o no) como Desarrollo Inmobiliario."}
              </div>
            </div>

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Datos del desarrollo (los pide el aviso DIN)</p>
              <Fila k="Registro / licencia de obra" v={sel.registro_licencia ?? "—"} />
              <Fila k="Entidad federativa (clave)" v={sel.entidad_federativa ?? "—"} />
              <Fila k="Tipo de desarrollo" v={TIPOS_DESARROLLO.find(([v]) => v === sel.tipo_desarrollo)?.[1] ?? sel.tipo_desarrollo ?? "—"} />
              <Fila k="Ubicación" v={[sel.calle, sel.colonia, sel.codigo_postal].filter((x): x is string => !!x).join(", ") || "—"} />
              <Fila k="Monto del desarrollo" v={dinero(sel.monto_desarrollo)} />
              <Fila k="Unidades comercializadas" v={sel.unidades_comercializadas === null ? "—" : String(sel.unidades_comercializadas)} />
              <Fila k="Costo por unidad" v={dinero(sel.costo_unidad)} />
              <Fila k="¿Participan otras empresas?" v={sel.otras_empresas ? "Sí" : "No"} />
            </div>

            {(sel.finalidad === "renta" || sel.finalidad === "mixto") && (
              <div style={{ ...st.tarjeta, background: C.amberBg, borderColor: "#F0D9B5" }}>
                <div style={{ fontSize: 12.5, color: "#6d4a00" }}>
                  <b>Build-to-rent:</b> las unidades en renta pueden activar la fr. XV con
                  umbrales mensuales propios y más bajos. Argus alertará al registrar el primer
                  cobro de renta.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoProyecto size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona un proyecto</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              Caracterizarlo antes del primer peso es la primera obligación: de ahí se derivan
              las fracciones aplicables y las altas en el Padrón.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Alta de proyecto — caracterización primero
// ─────────────────────────────────────────────────────────────────────────────

function FormProyecto({
  demo,
  onCrear,
  onCancelar,
}: {
  demo: boolean;
  onCrear: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}) {
  const [f, setF] = useState<Record<string, string>>({ finalidad: "venta", tipo_desarrollo: "1" });
  // #36: manifestación legal DIN <otras_empresas>. Sin default silencioso: se
  // captura explícitamente aquí y se declara al armar el alta (undefined = sin
  // contestar → el alta no está lista).
  const [otrasEmpresas, setOtrasEmpresas] = useState<boolean | null>(null);
  const [guardando, setGuardando] = useState(false);

  const campo = (k: string) => f[k] ?? "";
  const setCampo = (k: string, v: string) => { setF((prev) => ({ ...prev, [k]: v })); };
  const listo = campo("nombre").trim() !== "" && campo("justificacion").trim() !== ""
    && campo("jurisdiccion").trim() !== "" && otrasEmpresas !== null;

  const armar = (): Record<string, unknown> => ({
    nombre: campo("nombre").trim(),
    es_desarrollo_inmobiliario: true,
    justificacion_sellada: campo("justificacion").trim(),
    finalidad: campo("finalidad"),
    jurisdiccion: campo("jurisdiccion").trim(),
    registro_licencia: campo("licencia").trim() || null,
    entidad_federativa: campo("entidad").trim() || null,
    codigo_postal: campo("cp").trim() || null,
    colonia: campo("colonia").trim() || null,
    calle: campo("calle").trim() || null,
    tipo_desarrollo: campo("tipo_desarrollo"),
    monto_desarrollo: campo("monto") === "" ? null : Number(campo("monto")),
    unidades_comercializadas: campo("unidades") === "" ? null : Number(campo("unidades")),
    costo_unidad: campo("costo_unidad") === "" ? null : Number(campo("costo_unidad")),
    otras_empresas: otrasEmpresas,
  });

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <button onClick={onCancelar} style={{ border: "none", background: "transparent", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 8 }}>
        ‹ Cancelar
      </button>
      <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>Nuevo proyecto</h2>
      <p style={{ margin: "0 0 14px", color: C.muted, fontSize: 13 }}>
        Caracterízalo ANTES de recibir el primer peso: de aquí se derivan las fracciones
        aplicables y las altas en el Padrón.
      </p>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Identidad y destino</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Nombre del proyecto</label>
            <input style={st.input} placeholder="Ej. Torre Norte II" value={campo("nombre")} onChange={(e) => { setCampo("nombre", e.target.value); }} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Destino de las unidades</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {FINALIDADES.map(([v, t]) => (
                <button
                  key={v}
                  onClick={() => { setCampo("finalidad", v); }}
                  style={{
                    ...st.btn,
                    borderColor: campo("finalidad") === v ? C.tiel : C.line,
                    background: campo("finalidad") === v ? C.tielSoft : "#fff",
                    color: campo("finalidad") === v ? C.tielDark : C.ink,
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={st.label}>Jurisdicción (estado)</label>
            <input style={st.input} placeholder="YUC" value={campo("jurisdiccion")} onChange={(e) => { setCampo("jurisdiccion", e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Entidad federativa (clave DIN)</label>
            <input style={st.input} placeholder="31" inputMode="numeric" value={campo("entidad")} onChange={(e) => { setCampo("entidad", e.target.value.replace(/[^0-9]/g, "")); }} />
          </div>
        </div>
        <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 10, background: C.tielSoft, fontSize: 12.5, color: C.tielDark }}>
          Fracciones que se derivan: <b>{fraccionesDe(campo("finalidad")).join(" · ")}</b>
        </div>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Caracterización (se sella con fecha y usuario)</p>
        <label style={st.label}>¿Por qué encuadra como Desarrollo Inmobiliario? (Art. 3 fr. IV Bis)</label>
        <textarea
          style={{ ...st.input, minHeight: 72 }}
          placeholder="Construcción / fraccionamiento de lotes destinado a venta o renta…"
          value={campo("justificacion")}
          onChange={(e) => { setCampo("justificacion", e.target.value); }}
        />
        <p style={{ margin: "6px 0 0", fontSize: 12, color: C.muted }}>
          Es la primera evidencia del expediente del proyecto — queda sellada y se conserva 10 años.
        </p>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Datos del desarrollo (los pide el aviso DIN)</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Registro / licencia de obra</label>
            <input style={st.input} placeholder="LIC-…" value={campo("licencia")} onChange={(e) => { setCampo("licencia", e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Tipo de desarrollo</label>
            <select style={st.input} value={campo("tipo_desarrollo")} onChange={(e) => { setCampo("tipo_desarrollo", e.target.value); }}>
              {TIPOS_DESARROLLO.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Calle</label>
            <input style={st.input} value={campo("calle")} onChange={(e) => { setCampo("calle", e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Colonia</label>
            <input style={st.input} value={campo("colonia")} onChange={(e) => { setCampo("colonia", e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Código postal</label>
            <input style={st.input} inputMode="numeric" value={campo("cp")} onChange={(e) => { setCampo("cp", e.target.value.replace(/[^0-9]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Unidades totales</label>
            <input style={st.input} inputMode="numeric" value={campo("unidades")} onChange={(e) => { setCampo("unidades", e.target.value.replace(/[^0-9]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Monto proyectado</label>
            <input style={st.input} inputMode="numeric" placeholder="$" value={campo("monto")} onChange={(e) => { setCampo("monto", e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Costo por unidad</label>
            <input style={st.input} inputMode="numeric" placeholder="$" value={campo("costo_unidad")} onChange={(e) => { setCampo("costo_unidad", e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
        </div>
      </div>

      {/* #36: manifestación legal <otras_empresas> del aviso DIN — pregunta
          explícita, sin default silencioso. Se emite obligatoria en el XML. */}
      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Manifestación (la exige el aviso DIN)</p>
        <label style={{ ...st.label, marginBottom: 6 }}>
          ¿Participan otras empresas en el desarrollo?
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          {([["si", true], ["no", false]] as [string, boolean][]).map(([k, v]) => (
            <button
              key={k}
              onClick={() => { setOtrasEmpresas(v); }}
              style={{
                ...st.btn,
                borderColor: otrasEmpresas === v ? C.tiel : C.line,
                background: otrasEmpresas === v ? C.tielSoft : "#fff",
                color: otrasEmpresas === v ? C.tielDark : C.ink,
              }}
            >
              {v ? "Sí" : "No"}
            </button>
          ))}
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 12, color: C.muted }}>
          Es una manifestación legal: se declara en el aviso y queda en el expediente. No tiene valor por defecto.
        </p>
      </div>

      <button
        style={{ ...st.btnPrim, width: "100%", opacity: guardando || !listo ? OPACIDAD_DESHABILITADO : 1 }}
        disabled={guardando || !listo}
        onClick={() => {
          setGuardando(true);
          void onCrear(armar()).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Guardando…" : demo ? "Crear (requiere backend)" : "Crear proyecto y sellar caracterización"}
      </button>
      <p style={{ fontSize: 12, color: C.muted, textAlign: "center", margin: "10px 0 24px" }}>
        Sin justificación no se puede crear — la caracterización es la primera obligación.
      </p>
    </div>
  );
}
